import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import GitHubModal from './integrations/GitHubModal';
import GitHubViewModal from './integrations/GithubViewModal';
import { useUserMetadata } from '../context/UserMetadataContext';
import JiraModal from './integrations/JiraModal';
import JiraViewModal from './integrations/JiraViewModal';
import EntraIDModal from './integrations/EntraIDModal';
import EntraIDViewModal from './integrations/EntraIDViewModal';
import OktaModal from './integrations/OktaModal';
import OktaViewModal from './integrations/OktaViewModal';
import IntegrationStats from './integrations/IntegrationStats';
import IntegrationCard from './integrations/IntegrationCard';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GitHubIcon from '@mui/icons-material/GitHub';
import CloudIcon from '@mui/icons-material/Cloud';
import MessageIcon from '@mui/icons-material/Message';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SecurityIcon from '@mui/icons-material/Security';

const Integrations = () => {
  const { userMetadata } = useUserMetadata();
  const { getAccessTokenSilently, loginWithPopup } = useAuth0();
  const [githubOrgs, setGithubOrgs] = useState([]);
  const [jiraOrgs, setJiraOrgs] = useState([]);
  const [isGitHubModalOpen, setIsGitHubModalOpen] = useState(false);
  const [isGitHubViewModalOpen, setIsGitHubViewModalOpen] = useState(false);
  const [isJiraModalOpen, setIsJiraModalOpen] = useState(false);
  const [isJiraViewModalOpen, setIsJiraViewModalOpen] = useState(false);
  const [loadingGithub, setLoadingGithub] = useState(false);
  const [loadingJira, setLoadingJira] = useState(false);
  const [isEntraIDModalOpen, setIsEntraIDModalOpen] = useState(false);
  const [isEntraIDViewModalOpen, setIsEntraIDViewModalOpen] = useState(false);
  const [entraIDOrgs, setEntraIDOrgs] = useState([]);
  const [loadingEntraID, setLoadingEntraID] = useState(false);
  const [isOktaModalOpen, setIsOktaModalOpen] = useState(false);
  const [isOktaViewModalOpen, setIsOktaViewModalOpen] = useState(false);
  const [oktaOrgs, setOktaOrgs] = useState([]);
  const [loadingOkta, setLoadingOkta] = useState(false);
  const [syncingIntegrations, setSyncingIntegrations] = useState(new Set());

  const fetchIntegration = useCallback(async (type) => {
    if (!userMetadata) return;

    setLoadingGithub(true);
    setLoadingJira(true);
    setLoadingEntraID(true);
    setLoadingOkta(true);

    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
    const apiAudience = process.env.REACT_APP_API_AUDIENCE;

    if (!graphqlEndpoint || !apiAudience) {
      console.error('GraphQL endpoint or API audience not configured');
      setLoadingGithub(false);
      setLoadingJira(false);
      setLoadingEntraID(false);
      setLoadingOkta(false);
      return;
    }

    let accessToken;
    try {
      accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: apiAudience,
          scope: 'openid profile email',
        },
      });
    } catch (error) {
      if (error.message.includes('Missing Refresh Token')) {
        await loginWithPopup({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
            scope: 'openid profile email',
          },
        });
        setLoadingGithub(false);
        setLoadingJira(false);
        setLoadingEntraID(false);
        setLoadingOkta(false);
        return;
      }
      throw error;
    }

    try {
      const query = `
        query ListIntegrations($type: IntegrationType, $limit: Int) {
          listIntegrations(type: $type, limit: $limit) {
            items {
              id
              tenantId
              integrationType
              orgName
              status
              secretArn
              s3Bucket
              s3Key
              metadata
              createdAt
              updatedAt
              lastSyncAt
            }
            nextToken
          }
        }
      `;

      const variables = { limit: 100 };
      if (type) variables.type = type.toLowerCase();

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0]?.message || 'GraphQL query failed');
      }

      const integrations = result.data?.listIntegrations?.items || [];
      console.log('Fetched integrations via GraphQL:', integrations);

      const processIntegration = (integration) => ({
        id: integration.id,
        name: integration.orgName,
        date: integration.createdAt ? new Date(integration.createdAt).toLocaleDateString() : '-',
        status: integration.status || '-',
        comment: '-',
        repos: integration.metadata?.repos || [],
        in_scope_repos: integration.metadata?.in_scope_repos || [],
      });

      if (type) {
        const typeFilteredIntegrations = integrations
          .filter((i) => i.integrationType.toLowerCase() === type.toLowerCase())
          .map(processIntegration);

        if (type.toLowerCase() === 'github') setGithubOrgs(typeFilteredIntegrations);
        else if (type.toLowerCase() === 'jira') setJiraOrgs(typeFilteredIntegrations);
        else if (type.toLowerCase() === 'entra' || type.toLowerCase() === 'entraid')
          setEntraIDOrgs(typeFilteredIntegrations);
        else if (type.toLowerCase() === 'okta') setOktaOrgs(typeFilteredIntegrations);
      } else {
        setGithubOrgs(integrations.filter((i) => i.integrationType === 'github').map(processIntegration));
        setJiraOrgs(integrations.filter((i) => i.integrationType === 'jira').map(processIntegration));
        setEntraIDOrgs(integrations.filter((i) => i.integrationType === 'entra').map(processIntegration));
        setOktaOrgs(integrations.filter((i) => i.integrationType === 'okta').map(processIntegration));
      }
    } catch (error) {
      console.error('Error fetching integrations via GraphQL:', error);
    } finally {
      setLoadingGithub(false);
      setLoadingJira(false);
      setLoadingEntraID(false);
      setLoadingOkta(false);
    }
  }, [userMetadata, loginWithPopup, getAccessTokenSilently]);

  useEffect(() => {
    if (!userMetadata) return;
    (async () => {
      await fetchIntegration();
      await Promise.all([
        fetchIntegration('github'),
        fetchIntegration('jira'),
        fetchIntegration('entra'),
        fetchIntegration('okta'),
      ]);
    })();
  }, [userMetadata, fetchIntegration]);

  const handleGitHubSuccess = () => fetchIntegration('github');
  const handleJiraSuccess = () => fetchIntegration('jira');
  const handleEntraIDSuccess = () => fetchIntegration('entra');
  const handleOktaSuccess = () => fetchIntegration('okta');

  const pollSyncStatus = async (integrationId, accessToken, graphqlEndpoint, orgName, integrationType) => {
    const query = `
      query GetSyncStatus($id: ID!) {
        getSyncStatus(id: $id) {
          status
          message
          itemsProcessed
          error
        }
      }
    `;

    const maxAttempts = 60;
    const pollInterval = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ query, variables: { id: integrationId } }),
        });

        const responseData = await response.json();

        if (responseData.errors) {
          console.error('Error checking sync status:', responseData.errors);
          continue;
        }

        const status = responseData.data?.getSyncStatus;
        console.log(`Sync status (attempt ${attempt + 1}):`, status);

        if (status?.status === 'COMPLETED') {
          console.log(`Sync completed successfully! Items processed: ${status.itemsProcessed}`);
          alert(`✓ Sync completed for ${orgName}!\n${status.itemsProcessed} items processed.`);
          return;
        } else if (status?.status === 'FAILED') {
          throw new Error(status.error || 'Sync failed');
        }

        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      } catch (error) {
        console.error('Error polling sync status:', error);
        throw error;
      }
    }

    console.warn('Sync status polling timed out after 2 minutes');
    alert(`⚠ Sync for ${orgName} is still in progress.\nCheck back later for results.`);
  };

  const handleSync = async (integrationId, integrationType, orgName) => {
    setSyncingIntegrations((prev) => new Set(prev).add(integrationId));
    console.log(`Sync started for ${orgName} (${integrationId})`);

    try {
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
      const apiAudience = process.env.REACT_APP_API_AUDIENCE;

      if (!graphqlEndpoint || !apiAudience) {
        console.error('GraphQL endpoint or API audience not configured');
        alert('Configuration error. Please check console.');
        return;
      }

      const accessToken = await getAccessTokenSilently({
        authorizationParams: { audience: apiAudience, scope: 'openid profile email' },
      });

      const mutation = `
        mutation SyncIntegration($id: ID!) {
          syncIntegration(id: $id) {
            success
            message
            syncedAt
            itemsProcessed
          }
        }
      `;

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ query: mutation, variables: { id: integrationId } }),
      });

      const responseData = await response.json();

      if (responseData.errors) {
        console.error('GraphQL errors:', responseData.errors);
        throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
      }

      const result = responseData.data?.syncIntegration;
      if (!result || !result.success) {
        throw new Error(result?.message || 'Failed to sync integration');
      }

      await pollSyncStatus(integrationId, accessToken, graphqlEndpoint, orgName, integrationType);
      await fetchIntegration(integrationType);
    } catch (error) {
      console.error('Error syncing integration:', error);
      alert(`✗ Sync failed for ${orgName}:\n${error.message}`);
    } finally {
      setSyncingIntegrations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(integrationId);
        return newSet;
      });
    }
  };

  const integrationsList = [
    {
      name: 'GitHub',
      description: 'Connect your GitHub repositories for seamless integration.',
      status: 'Available',
      icon: <GitHubIcon />,
      onClick: () => setIsGitHubModalOpen(true),
      onViewClick: () => setIsGitHubViewModalOpen(true),
      supportSync: true,
      singularLabel: 'organization',
      pluralLabel: 'organizations',
      type: 'github',
    },
    {
      name: 'Slack',
      description: 'Get notifications and updates directly in your Slack workspace.',
      status: 'Coming Soon',
      icon: <MessageIcon />,
      type: 'slack',
    },
    {
      name: 'JIRA',
      description: 'Sync your project tracking with JIRA integration.',
      status: 'Available',
      icon: <AssignmentIcon />,
      onClick: () => setIsJiraModalOpen(true),
      onViewClick: () => setIsJiraViewModalOpen(true),
      supportSync: true,
      singularLabel: 'instance',
      pluralLabel: 'instances',
      type: 'jira',
    },
    {
      name: 'Microsoft Entra ID',
      description: 'Connect your Microsoft Entra ID for identity and access management.',
      status: 'Available',
      icon: <CloudIcon />,
      onClick: () => setIsEntraIDModalOpen(true),
      onViewClick: () => setIsEntraIDViewModalOpen(true),
      supportSync: true,
      singularLabel: 'tenant',
      pluralLabel: 'tenants',
      type: 'entra',
    },
    {
      name: 'Okta',
      description: 'Connect your Okta tenant for identity and access management.',
      status: 'Available',
      icon: <SecurityIcon />,
      onClick: () => setIsOktaModalOpen(true),
      onViewClick: () => setIsOktaViewModalOpen(true),
      supportSync: true,
      singularLabel: 'tenant',
      pluralLabel: 'tenants',
      type: 'okta',
    },
  ];

  const getConnectedItems = (type) => {
    const typeMap = {
      github: githubOrgs,
      jira: jiraOrgs,
      entra: entraIDOrgs,
      okta: oktaOrgs,
    };
    return typeMap[type] || [];
  };

  const calculateStats = () => {
    const connected = integrationsList.filter((i) => getConnectedItems(i.type).length > 0).length;
    const available = integrationsList.filter((i) => i.status === 'Available').length;

    let mostRecentSync = null;
    [...githubOrgs, ...jiraOrgs, ...entraIDOrgs, ...oktaOrgs].forEach((org) => {
      if (org.lastSyncAt) {
        const syncDate = new Date(org.lastSyncAt);
        if (!mostRecentSync || syncDate > mostRecentSync) mostRecentSync = syncDate;
      }
    });

    const formatLastSync = (date) => {
      if (!date) return 'Never';
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    };

    return {
      connected,
      available,
      total: integrationsList.length,
      lastSync: formatLastSync(mostRecentSync),
    };
  };

  const stats = calculateStats();

  const connectedIntegrations = integrationsList.filter((i) => getConnectedItems(i.type).length > 0);
  const availableIntegrations = integrationsList.filter(
    (i) => i.status === 'Available' && getConnectedItems(i.type).length === 0,
  );
  const comingSoonIntegrations = integrationsList.filter((i) => i.status === 'Coming Soon');

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Integrations Overview
      </Typography>

      <IntegrationStats
        connected={stats.connected}
        available={stats.available}
        total={stats.total}
        lastSync={stats.lastSync}
      />

      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Connected */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Connected
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {connectedIntegrations.length > 0 ? (
              connectedIntegrations.map((integration, index) => (
                <IntegrationCard
                  key={index}
                  integration={integration}
                  isConnected={true}
                  onManage={integration.onViewClick}
                  onConnect={integration.onClick}
                  onSync={(integrationId, orgName) => handleSync(integrationId, integration.type, orgName)}
                  isLoading={
                    (integration.type === 'github' && loadingGithub) ||
                    (integration.type === 'jira' && loadingJira) ||
                    (integration.type === 'entra' && loadingEntraID) ||
                    (integration.type === 'okta' && loadingOkta)
                  }
                  syncingIntegrations={syncingIntegrations}
                  connectedItems={getConnectedItems(integration.type)}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No integrations connected yet
              </Typography>
            )}
          </Box>
        </Box>

        {/* Available */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Available
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {availableIntegrations.map((integration, index) => (
              <IntegrationCard
                key={index}
                integration={integration}
                isConnected={false}
                onConnect={integration.onClick}
                isLoading={false}
              />
            ))}
          </Box>
        </Box>

        {/* Coming Soon */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Coming Soon
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {comingSoonIntegrations.map((integration, index) => (
              <IntegrationCard
                key={index}
                integration={integration}
                isConnected={false}
                onConnect={() => {}}
                isLoading={false}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <GitHubModal
        isOpen={isGitHubModalOpen}
        onClose={() => setIsGitHubModalOpen(false)}
        onSuccess={handleGitHubSuccess}
      />
      <GitHubViewModal
        isOpen={isGitHubViewModalOpen}
        onClose={() => setIsGitHubViewModalOpen(false)}
        orgs={githubOrgs}
        setOrgs={setGithubOrgs}
        onRefresh={() => fetchIntegration('github')}
      />
      <JiraModal
        isOpen={isJiraModalOpen}
        onClose={() => setIsJiraModalOpen(false)}
        onSuccess={handleJiraSuccess}
      />
      <JiraViewModal
        isOpen={isJiraViewModalOpen}
        onClose={() => setIsJiraViewModalOpen(false)}
        orgs={jiraOrgs}
        setOrgs={setJiraOrgs}
        onRefresh={() => fetchIntegration('jira')}
      />
      <EntraIDModal
        isOpen={isEntraIDModalOpen}
        onClose={() => setIsEntraIDModalOpen(false)}
        onSuccess={handleEntraIDSuccess}
      />
      <OktaModal
        isOpen={isOktaModalOpen}
        onClose={() => setIsOktaModalOpen(false)}
        onSuccess={handleOktaSuccess}
      />
      <EntraIDViewModal
        isOpen={isEntraIDViewModalOpen}
        onClose={() => setIsEntraIDViewModalOpen(false)}
        orgs={entraIDOrgs}
        setOrgs={setEntraIDOrgs}
        onRefresh={() => fetchIntegration('entra')}
      />
      <OktaViewModal
        isOpen={isOktaViewModalOpen}
        onClose={() => setIsOktaViewModalOpen(false)}
        orgs={oktaOrgs}
        setOrgs={setOktaOrgs}
        onRefresh={() => fetchIntegration('okta')}
      />
    </Box>
  );
};

export default Integrations;
